import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
  B2C_SESSION_HINT_KEY,
  clearSessionHint,
  fetchSessionMe,
  hasSessionHint,
  setSessionHint,
} from '../utils/authSession';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (!hasSessionHint(B2C_SESSION_HINT_KEY)) {
      setLoading(false);
      return;
    }
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetchSessionMe(`${API}/auth/me`);
      if (response.status === 401) {
        clearSessionHint(B2C_SESSION_HINT_KEY);
        setUser(null);
        setIsAuthenticated(false);
        return;
      }
      setUser(response.data);
      setIsAuthenticated(true);
      setSessionHint(B2C_SESSION_HINT_KEY);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    setSessionHint(B2C_SESSION_HINT_KEY);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearSessionHint(B2C_SESSION_HINT_KEY);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = (updatedUserData) => {
    const newUser = { ...user, ...updatedUserData };
    setUser(newUser);
  };

  const getSavedPassengers = async () => {
    if (!isAuthenticated) return [];

    try {
      const response = await axios.get(`${API}/user/passengers`);
      return response.data.passengers || [];
    } catch (error) {
      console.error('Failed to fetch saved passengers:', error);
      return [];
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    getSavedPassengers,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
